import { useState, useEffect, useCallback } from 'react';
import { api } from '../api';
import {
  PageHeader, Card, Stat, Field, Select,
  Table, THead, TH, TR, TD, Empty, InfoDot, SourceNote,
} from '../components/ui';
import { BillingEntityBadge } from '../components/BillingEntity';

const R = (v) => `R${Number(v || 0).toLocaleString('en-ZA', { maximumFractionDigits: 0 })}`;
const fmtDate = (d) => (d ? new Date(`${d}T00:00:00Z`).toLocaleDateString('en-ZA', { day: 'numeric', month: 'short', year: '2-digit' }) : '—');

const REVENUE_SOURCES = [
  { source: 'Xero', label: 'Invoiced', detail: 'Σ AR invoice totals (ACCREC) issued in the month — read-only from Xero' },
  { source: 'Xero', label: 'Collected', detail: 'invoiced − outstanding, i.e. the paid portion' },
  { source: 'Xero', label: 'Outstanding / AR', detail: 'amount still due on open (authorised, unpaid) invoices' },
  { source: 'Xero', label: 'DSO', detail: 'days sales outstanding = AR ÷ trailing-365-day invoiced × 365' },
  { source: 'Magnetic', label: 'Expected recurring', detail: 'Σ monthly_amount of active retainer projects — the billing benchmark' },
];

const TABS = [['entity', 'By entity'], ['clients', 'By client'], ['ar', 'Outstanding']];

export default function Revenue() {
  const [months, setMonths] = useState([]);
  const [month, setMonth] = useState('');
  const [summary, setSummary] = useState(null);
  const [recon, setRecon] = useState([]);
  const [clients, setClients] = useState([]);
  const [ar, setAr] = useState(null);
  const [tab, setTab] = useState('entity');

  useEffect(() => { api.revenue.months().then((m) => { setMonths(m); setMonth(m[0] || ''); }).catch(() => {}); }, []);
  useEffect(() => { api.revenue.ar().then(setAr).catch(() => {}); }, []);

  const load = useCallback(() => {
    if (!month) return;
    api.revenue.summary(month).then(setSummary).catch(() => {});
    api.revenue.reconciliation(month).then((d) => setRecon(d.rows || [])).catch(() => {});
    api.revenue.clients(month).then(setClients).catch(() => {});
  }, [month]);
  useEffect(() => { load(); }, [load]);

  const b = ar?.buckets || {};
  const AGING = [
    ['Current', b.current], ['1–30', b.d1_30], ['31–60', b.d31_60],
    ['61–90', b.d61_90], ['90+', b.d90_plus],
  ];

  return (
    <div>
      <PageHeader
        eyebrow="Operations"
        title="Revenue & Accounts Receivable"
        subtitle="Actual invoiced revenue and outstanding AR from Xero, per legal entity and client. Read-only — the accounting source of truth alongside Magnetic delivery data."
        actions={
          <Field label="Month">
            <Select value={month} onChange={(e) => setMonth(e.target.value)}>
              {months.map((m) => <option key={m} value={m}>{m}</option>)}
            </Select>
          </Field>
        }
      />

      <SourceNote items={REVENUE_SOURCES} />

      {summary && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-8">
          <Stat label="Invoiced" value={R(summary.invoiced)} sub={`${summary.invoices} invoices · ${month}`}
            info="Σ AR invoice totals issued this month (Xero, incl. tax)." />
          <Stat label="Collected" value={R(summary.collected)} sub="paid portion this month"
            info="Invoiced − still-outstanding for invoices issued this month." />
          <Stat label="Outstanding (AR)" value={R(summary.outstanding_total)} sub={`${summary.open_count} open invoices`}
            info="Total amount due across all open invoices — not just this month." />
          <Stat label="DSO" value={ar ? `${ar.dso} days` : '…'} sub="days sales outstanding"
            info="AR ÷ trailing-365-day invoiced × 365. Lower = faster collection." />
          <Stat label="90+ overdue" value={R(b.d90_plus)} sub="oldest AR bucket"
            info="Amount due on invoices more than 90 days past their due date." />
        </div>
      )}

      {/* AR aging strip */}
      {ar && (
        <Card title="AR aging" className="mb-8" padding="p-5"
          subtitle={`Total outstanding ${R(b.total)} · DSO ${ar.dso} days`}>
          <div className="grid grid-cols-5 gap-3 mt-2">
            {AGING.map(([label, val]) => {
              const pct = b.total ? Math.round((Number(val || 0) / Number(b.total)) * 100) : 0;
              const overdue = label !== 'Current';
              return (
                <div key={label} className="rounded-lg border border-slate-700 bg-slate-800 p-3">
                  <p className="text-[11px] uppercase tracking-[0.1em] text-slate-500">{label}</p>
                  <p className={`text-lg font-semibold mt-1 ${overdue && Number(val) > 0 ? 'text-amber-400' : 'text-slate-100'}`}>{R(val)}</p>
                  <div className="h-1 rounded bg-slate-700 mt-2 overflow-hidden">
                    <div className={overdue ? 'h-full bg-amber-500' : 'h-full bg-brand-500'} style={{ width: `${pct}%` }} />
                  </div>
                </div>
              );
            })}
          </div>
        </Card>
      )}

      <nav className="flex gap-6 border-b border-slate-700 mb-6">
        {TABS.map(([id, label]) => (
          <button key={id} onClick={() => setTab(id)}
            className={`py-3 text-sm font-medium border-b-2 -mb-px ${tab === id ? 'border-brand-500 text-slate-100' : 'border-transparent text-slate-500 hover:text-slate-200'}`}>
            {label}
          </button>
        ))}
      </nav>

      {tab === 'entity' && (
        <Card padding="p-0" title="Invoiced vs expected, by legal entity"
          subtitle="Xero invoiced/collected this month vs the Magnetic recurring (retainer) expectation.">
          <Table>
            <THead>
              <TH>Entity</TH>
              <TH align="right">Invoiced</TH>
              <TH align="right">Collected</TH>
              <TH align="right"><span className="inline-flex items-center gap-1 justify-end">Expected recurring <InfoDot>Σ monthly_amount of active retainer projects for this entity (Magnetic).</InfoDot></span></TH>
              <TH align="right"><span className="inline-flex items-center gap-1 justify-end">Variance <InfoDot>Invoiced − expected recurring. Large negative = possible under-billing of retainers.</InfoDot></span></TH>
            </THead>
            <tbody>
              {recon.length === 0 ? (
                <TR><TD>{month ? 'No invoices this month.' : '…'}</TD></TR>
              ) : recon.map((r, i) => (
                <TR key={i}>
                  <TD><BillingEntityBadge name={r.entity} /></TD>
                  <TD align="right" mono>{R(r.invoiced)}</TD>
                  <TD align="right" mono muted>{R(r.collected)}</TD>
                  <TD align="right" mono muted>{r.expected_recurring ? R(r.expected_recurring) : '—'}</TD>
                  <TD align="right" mono className={r.expected_recurring && r.variance < 0 ? 'text-brand-500' : 'text-slate-200'}>
                    {r.expected_recurring ? `${r.variance > 0 ? '+' : ''}${R(r.variance)}` : '—'}
                  </TD>
                </TR>
              ))}
            </tbody>
          </Table>
        </Card>
      )}

      {tab === 'clients' && (
        <Card padding="p-0" title={`Revenue by client · ${month}`}>
          <Table>
            <THead>
              <TH>Client</TH><TH>Entity</TH>
              <TH align="right">Invoiced</TH><TH align="right">Outstanding</TH><TH align="right">Invoices</TH>
            </THead>
            <tbody>
              {clients.length === 0 ? <TR><TD>No invoices this month.</TD></TR> : clients.map((c, i) => (
                <TR key={i}>
                  <TD>{c.client}</TD>
                  <TD>{c.entity ? <BillingEntityBadge name={c.entity} /> : <span className="text-slate-600">—</span>}</TD>
                  <TD align="right" mono>{R(c.invoiced)}</TD>
                  <TD align="right" mono className={Number(c.outstanding) > 0 ? 'text-amber-400' : 'text-slate-500'}>{Number(c.outstanding) > 0 ? R(c.outstanding) : '—'}</TD>
                  <TD align="right" mono muted>{c.invoices}</TD>
                </TR>
              ))}
            </tbody>
          </Table>
        </Card>
      )}

      {tab === 'ar' && (
        <Card padding="p-0" title="Outstanding invoices"
          subtitle={ar ? `${ar.invoices.length} open · ${R(b.total)} due` : ''}>
          {!ar || ar.invoices.length === 0 ? <div className="px-4 py-6"><Empty>Nothing outstanding.</Empty></div> : (
            <Table>
              <THead>
                <TH>Invoice</TH><TH>Client</TH><TH>Entity</TH>
                <TH align="right">Amount</TH><TH align="right">Due</TH>
                <TH>Due date</TH><TH align="right">Days overdue</TH>
              </THead>
              <tbody>
                {ar.invoices.map((v, i) => (
                  <TR key={i}>
                    <TD mono>{v.number || '—'}</TD>
                    <TD>{v.contact_name || '—'}</TD>
                    <TD>{v.billing_entity_name ? <BillingEntityBadge name={v.billing_entity_name} /> : <span className="text-slate-600">—</span>}</TD>
                    <TD align="right" mono muted>{R(v.amount)}</TD>
                    <TD align="right" mono>{R(v.amount_due)}</TD>
                    <TD muted>{fmtDate(v.due_date)}</TD>
                    <TD align="right" mono className={v.days_overdue > 0 ? 'text-amber-400' : 'text-slate-500'}>{v.days_overdue > 0 ? v.days_overdue : '—'}</TD>
                  </TR>
                ))}
              </tbody>
            </Table>
          )}
        </Card>
      )}
    </div>
  );
}
